import { matchPath } from "react-router-dom";
import registry from "../config/features.json";

/**
 * Lookups over the feature registry (`config/features.json`, a synced copy of
 * `packages/shared/features.json` — never edit it by hand).
 *
 * The registry is the one place a route or an endpoint gets its tier; the web
 * gate (`AccessGate`), the action gate (`useRequireAuth`) and the sign-in
 * prompt all ask it rather than carrying their own lists.
 */

const FEATURES = new Map(registry.features.map((f) => [f.id, f]));

// Most specific first: more static segments, fewer params, then longer.
const specificity = (p) => [
  p.split("/").filter((s) => s && !s.startsWith(":")).length * -1,
  p.includes(":") ? 1 : 0,
  -p.length,
];
const ROUTES = registry.features
  .flatMap((f) => (f.routes || []).map((path) => ({ path, feature: f })))
  .sort((a, b) => {
    const [sa, sb] = [specificity(a.path), specificity(b.path)];
    return sa[0] - sb[0] || sa[1] - sb[1] || sa[2] - sb[2];
  });

// Mirrors access.UNCLASSIFIED on the API: the id a fail-closed prompt reports.
export const UNCLASSIFIED_ID = "unclassified";

// What the modal says when a feature carries no `prompt` of its own.
export const GENERIC_PROMPT = "continue";

/** A path the registry does not know is auth-required, never open. */
export const FAIL_CLOSED = Object.freeze({
  id: UNCLASSIFIED_ID,
  access: "auth-required",
  credentials: ["firebase"],
  guards: [],
  routes: [],
  prompt: GENERIC_PROMPT,
});

/**
 * The contaminant codes the API keeps in a shaped response — raw row codes,
 * so `HFPO-DA` sits beside `GENX` (see config/headlineCodesParity.test.js).
 */
export const HEADLINE_CODES = new Set(registry.shaping.headline_contaminant_codes);

export const getFeature = (id) => FEATURES.get(id) ?? null;
export const allRoutes = () => ROUTES.map((r) => r.path);
export const isRedirect = (pathname) =>
  Object.prototype.hasOwnProperty.call(registry.redirects, pathname);
// Tests and docs only; the gate never follows a redirect itself (see below).
export const redirectTarget = (pathname) => registry.redirects[pathname] ?? pathname;

/** The reason shown after "Sign in to …" for a feature (or an unknown id). */
export const promptFor = (featureOrId) => {
  const feature = typeof featureOrId === "string" ? getFeature(featureOrId) : featureOrId;
  return feature?.prompt || GENERIC_PROMPT;
};

/**
 * The feature governing `pathname`, or null if the registry does not know it.
 *
 * Does NOT follow redirects. `AccessGate` asks `isRedirect` first and lets a
 * redirect source render, so its `<Navigate>` runs and the target is
 * classified on the next render. Resolving `/my-properties` to its target's
 * tier here would render the sign-in shell at the old path and the redirect
 * would never happen.
 */
export const featureForPath = (pathname) => {
  const hit = ROUTES.find((r) => matchPath({ path: r.path, end: true }, pathname));
  return hit ? hit.feature : null;
};
